import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDisclosure } from '@aimsos/hooks';
import {
  dropdownMenuHeaderClass,
  dropdownMenuRow50Class,
  topbarTenantAvatarClass,
} from './topbarClasses.js';
import Avatar from '../Avatar.jsx';
import Skeleton from '../Skeleton.jsx';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

const SKELETON_ROW_COUNT = 4;
const SKELETON_KEYS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

const TenantRowSkeleton = () => (
  <div
    role="status"
    aria-label="Loading workspace"
    className={[dropdownMenuRow50Class, 'cursor-default'].join(' ')}
  >
    <Skeleton width={32} height={32} borderRadius="9999px" className="shrink-0" />
    <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
      <Skeleton width="60%" height={10} borderRadius="3px" />
      <Skeleton width="40%" height={8} borderRadius="3px" />
    </div>
  </div>
);

/**
 * Tenant/workspace switcher section inside the Topbar account dropdown.
 *
 * Supports two search modes:
 * - **Uncontrolled** (default): local front-end filtering on the `tenants` array.
 * - **Controlled**: when `searchValue` + `onSearchChange` are provided, the
 *   parent manages the query (e.g. backend search with debounce). Local
 *   filtering is disabled in this mode since the backend already filters.
 *
 * Infinite scroll is opt-in: pass `hasMore`, `onLoadMore`, and optionally
 * `isLoadingMore` to enable automatic next-page loading via IntersectionObserver.
 *
 * All new props are optional; omitting them preserves the existing behaviour.
 */
const TopbarTenantsSection = ({
  tenants = [],
  currentTenantId,
  onTenantSelect,
  onTenantSearch,
  organizationLabel = 'Organization',
  searchPlaceholder = 'Search',
  initialExpanded = false,
  isLoading = false,
  isSearchLoading = false,
  // Controlled search props
  searchValue,
  onSearchChange,
  // Infinite scroll props
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const { isOpen, toggle } = useDisclosure(initialExpanded);

  // Internal query state used only in uncontrolled mode.
  const [internalQuery, setInternalQuery] = useState('');
  const isControlled = searchValue !== undefined;
  const query = isControlled ? searchValue : internalQuery;

  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  // IntersectionObserver for infinite scroll — only active when isOpen and hasMore.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || !onLoadMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, isLoadingMore, isLoading, isOpen]);

  const currentTenant = tenants.find((tenant) => tenant.id === currentTenantId) || tenants[0];
  const secondaryLine = currentTenant?.role || '';

  // Front-end filter is only applied in uncontrolled mode — when the parent
  // drives search, the backend has already filtered the `tenants` list.
  const filteredTenants = useMemo(() => {
    if (isControlled) return tenants;
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (tenant) =>
        (tenant.name || '').toLowerCase().includes(q) ||
        (tenant.email || '').toLowerCase().includes(q) ||
        (tenant.role || '').toLowerCase().includes(q),
    );
  }, [tenants, query, isControlled]);

  const handleSearchChange = useCallback((event) => {
    const next = event.target.value;
    if (isControlled) {
      onSearchChange?.(next);
    } else {
      setInternalQuery(next);
    }
    onTenantSearch?.(next, event);
  }, [isControlled, onSearchChange, onTenantSearch]);

  return (
    <div className="w-full">
      <div className={dropdownMenuHeaderClass}>{organizationLabel}</div>
      {/* Always show the chip — skeleton only when there is truly no workspace
          name to display yet (e.g. before the very first API response and
          before the currentWorkspace detail resolves). During search refetches
          isLoading is also true but currentTenant already has a name from the
          previous results, so the chip stays visible and stable. */}
      {isLoading && !currentTenant?.name ? (
        <div
          role="status"
          aria-label="Loading workspace"
          className={[dropdownMenuRow50Class, 'cursor-default'].join(' ')}
        >
          <Skeleton width={32} height={32} borderRadius="9999px" className="shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <Skeleton width="55%" height={10} borderRadius="3px" />
            <Skeleton width="35%" height={8} borderRadius="3px" />
          </div>
          <Skeleton width={12} height={12} borderRadius="2px" className="shrink-0" />
        </div>
      ) : (
        <button
          type="button"
          className={[dropdownMenuRow50Class, !isOpen ? 'border-b border-edge' : '']
            .filter(Boolean)
            .join(' ')}
          onClick={toggle}
        >
          <Avatar
            size="lg"
            initials={currentTenant?.initials || 'TN'}
            className={[
              topbarTenantAvatarClass,
              '!h-8 !w-8 !min-h-[2rem] !min-w-[2rem] !text-[14px]',
            ].join(' ')}
          />
          <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            <span className="w-full truncate text-left text-[12px] font-semibold leading-none text-content-secondary">
              {currentTenant?.name || ''}
            </span>
            {secondaryLine ? (
              <span className="w-full truncate text-left text-[11px] leading-none text-content-muted">
                {secondaryLine}
              </span>
            ) : null}
          </div>
          {isOpen ? (
            <ChevronUp size={16} className="shrink-0 text-content-muted" aria-hidden />
          ) : (
            <ChevronDown size={16} className="shrink-0 text-content-muted" aria-hidden />
          )}
        </button>
      )}

      {isOpen ? (
        <div className="w-full">
          <div className="flex h-10 items-center gap-4 border-t border-edge px-4">
            <Search size={20} className="shrink-0 text-content-muted" aria-hidden />
            <input
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={handleSearchChange}
              aria-label={searchPlaceholder}
              className="h-full min-w-0 flex-1 bg-transparent text-[12px] text-content-secondary placeholder:text-content-faint focus:outline-none"
            />
          </div>
          <div
            ref={listRef}
            className="max-h-56 overflow-y-auto scrollbar-aimsos-auto-sm py-1"
            onMouseEnter={() => {
              if (listRef.current) {
                listRef.current.dataset.scrollbarHover = 'true';
                listRef.current.dataset.scrollbarVisible = 'true';
              }
            }}
            onMouseLeave={() => {
              if (listRef.current) {
                delete listRef.current.dataset.scrollbarHover;
                delete listRef.current.dataset.scrollbarVisible;
              }
            }}
          >
            {(isLoading && filteredTenants.length === 0) || isSearchLoading ? (
              SKELETON_KEYS.map((i) => (
                <TenantRowSkeleton key={`tenant-skeleton-${i}`} />
              ))
            ) : filteredTenants.length === 0 ? (
              <p className="px-4 py-3 text-center text-[12px] text-content-muted">
                {query.trim() ? `No matches for "${query.trim()}"` : 'No workspaces'}
              </p>
            ) : (
              <>
                {filteredTenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    type="button"
                    onClick={() => onTenantSelect?.(tenant)}
                    className={[
                      dropdownMenuRow50Class,
                      tenant.id === currentTenantId ? 'bg-surface-hover-subtle' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Avatar
                      size="lg"
                      initials={tenant.initials || 'TN'}
                      className="!h-8 !w-8 !min-h-[2rem] !min-w-[2rem] shrink-0 border border-[var(--border\/primary\/lighter,#80afff)] bg-[var(--surface\/primary\/default,#2173ff)] text-[12px] font-semibold text-[var(--text\/negative,#ffffff)]"
                    />
                    <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                      <span className="w-full truncate text-left text-[12px] font-semibold leading-none text-content-secondary">
                        {tenant.name}
                      </span>
                      {tenant.role ? (
                        <span className="w-full truncate text-left text-[11px] leading-none text-content-muted">
                          {tenant.role}
                        </span>
                      ) : null}
                    </div>
                  </button>
                ))}

                {/* Infinite scroll sentinel — observed by IntersectionObserver */}
                {(hasMore || isLoadingMore) && (
                  <div ref={sentinelRef}>
                    {isLoadingMore && <TenantRowSkeleton />}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TopbarTenantsSection;
